// React / Next
import { useState, FormEvent } from 'react';

// Components
import { Typography } from '@archieai/ui';

// Hooks
import {
  useHubSpotForm,
  HUBSPOT_NEWSLETTER_CONFIG,
} from '../../hooks/useHubSpotForm';
import { useTrackEvent } from '../../hooks/useTrackEvent';

// Utils
import { cn } from '../../utils/cn';
import { isPersonalEmail } from '../../utils/emailValidation';

// Types
type SignupCalloutProps = {
  /** Title text */
  title?: string;
  /** Label text above the title */
  label?: string;
  /** Placeholder text for the email input */
  placeholder?: string;
  /** Button text */
  buttonText?: string;
  /** Success title text */
  successTitle?: string;
  /** Success message text */
  successMessage?: string;
  /** Additional className for custom styling */
  className?: string;
};

export const SignupCallout = ({
  title = 'Stay in the Know.',
  label = 'Signup',
  placeholder = 'Work email address',
  buttonText = 'Subscribe',
  successTitle = 'You Are In!',
  successMessage =
    "Thanks for subscribing. We'll keep you updated with the Archie's latest news.",
  className,
}: SignupCalloutProps) => {
  const [email, setEmail] = useState('');
  const [validationError, setValidationError] = useState<string | null>(null);
  const trackEvent = useTrackEvent();

  const { submit, isSubmitting, isSuccess, error, reset } = useHubSpotForm(
    HUBSPOT_NEWSLETTER_CONFIG
  );

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setValidationError(null);

    if (!email.trim()) return;

    if (isPersonalEmail(email)) {
      setValidationError('Please use your work email address.');
      return;
    }

    const success = await submit([{ name: 'email', value: email }]);

    if (success) {
      trackEvent('form_submitted', {
        form_name: 'newsletter',
        form_location: 'signup_callout',
      });
      setEmail('');
    }
  };

  const handleEmailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setEmail(e.target.value);
    setValidationError(null);
    if (error) {
      reset();
    }
  };

  const displayError = validationError || error;

  // Success state
  if (isSuccess) {
    return (
      <div
        className={cn(
          'flex items-center justify-center bg-blue-1000 rounded-12 overflow-hidden px-[var(--spacing-32)] py-[var(--spacing-32)] w-full max-w-[1016px]',
          className
        )}
      >
        <div className="flex items-center gap-[var(--spacing-16)]">
          <div className="w-10 h-10 rounded-full bg-green-1000/20 flex items-center justify-center shrink-0">
            <svg
              width="20"
              height="20"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              className="text-green-1000"
              aria-hidden="true"
            >
              <polyline points="20 6 9 17 4 12" />
            </svg>
          </div>
          <div className="flex flex-col gap-[var(--spacing-4)]">
            <h2 className="font-heading text-[24px] leading-[32px] font-light text-orange-200">
              {successTitle}
            </h2>
            <p className="font-body text-[14px] leading-[20px] text-gray-200">
              {successMessage}
            </p>
          </div>
          <button
            type="button"
            onClick={reset}
            className="ml-[var(--spacing-16)] font-body text-[14px] leading-[20px] text-blue-800 hover:text-blue-400 underline underline-offset-2 transition-colors shrink-0"
          >
            Subscribe another
          </button>
        </div>
      </div>
    );
  }

  return (
    <div
      className={cn(
        'flex items-center justify-between bg-blue-1000 rounded-12 overflow-hidden px-[var(--spacing-32)] py-[var(--spacing-32)] w-full max-w-[1016px]',
        'max-md:flex-col max-md:gap-[var(--spacing-24)] max-md:items-start',
        className
      )}
    >
      {/* Left side: Label + Title */}
      <div className="flex flex-col gap-[var(--spacing-8)]">
        <Typography
          variant="column-header"
          className="text-blue-800 uppercase tracking-wide"
        >
          {label}
        </Typography>
        <h2 className="font-heading text-[24px] leading-[32px] font-light text-orange-200">
          {title}
        </h2>
      </div>

      {/* Right side: Email form */}
      <form
        onSubmit={handleSubmit}
        className="flex flex-col gap-[var(--spacing-8)] max-md:w-full"
      >
        <div className="flex items-center gap-[var(--spacing-8)] max-md:w-full">
          <input
            type="email"
            value={email}
            onChange={handleEmailChange}
            placeholder={placeholder}
            disabled={isSubmitting}
            aria-label={placeholder}
            aria-invalid={!!displayError}
            aria-describedby={displayError ? 'signup-callout-error' : undefined}
            className={cn(
              'w-[324px] h-[48px] px-[var(--spacing-16)] py-[var(--spacing-12)]',
              'bg-transparent border rounded-8',
              displayError ? 'border-red-1000' : 'border-gray-200',
              'font-body text-[16px] leading-[24px] text-white placeholder:text-gray-600',
              'focus:outline-none focus:border-blue-800 transition-colors',
              'disabled:opacity-50 disabled:cursor-not-allowed',
              'max-md:flex-1 max-md:w-auto'
            )}
          />
          <button
            type="submit"
            disabled={isSubmitting || !email.trim()}
            className={cn(
              'inline-flex items-center justify-center',
              'px-[var(--spacing-16)] py-[var(--spacing-12)] rounded-8',
              'bg-orange-800 text-white',
              'font-body text-[16px] leading-[24px] font-normal',
              'hover:bg-orange-1000 transition-colors duration-200',
              'focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-orange-800',
              'disabled:opacity-50 disabled:cursor-not-allowed',
              'shrink-0'
            )}
          >
            {isSubmitting ? 'Subscribing...' : buttonText}
          </button>
        </div>

        {displayError && (
          <p
            id="signup-callout-error"
            className="font-body text-[14px] leading-[20px] text-red-1000"
            role="alert"
          >
            {displayError}
          </p>
        )}
      </form>
    </div>
  );
};
